import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'src/app/store';
import { LinkProps } from '../../../commons/utils/types';
import { LocalNotification } from '../components/Banners/types';
import {
  CommunicationsDrawerInfo,
  CurrentAccountInfo,
  SignOutInfo,
  SpoofingInfo,
  UserInfo,
} from '@amzn/aac-chrome-internal-api';

// TODO: remove this export statement
export {
  CommunicationsDrawerInfo,
  SpoofingInfo,
  UserInfo,
  LanguageInfo,
  SignOutInfo,
} from '@amzn/aac-chrome-internal-api';

export interface AjaxInfo {
  endpoint: string;
}

// Define a type for the slice state
export interface HeaderState {
  hieConsoleLevelBannerCdn: string;
  userInfo: UserInfo;
  helpInfo: Array<LinkProps>;
  currentAccountInfo?: CurrentAccountInfo;
  ajaxInfo?: AjaxInfo;
  spoofingInfo: SpoofingInfo;
  pageLayoutType?: string;
  variant?: string;
  consoleLevelBanners?: LocalNotification[];
  communicationsDrawerInfo?: CommunicationsDrawerInfo;
  isBackUpNav: boolean;
  pageTitleBreadcrumb?: string;

  // Redux local states
  hasSalesAssistantLoaded?: boolean;
}

// Define the initial state using that type
export const headerInitialState: HeaderState = {
  userInfo: {
    customerInfo: {
      fullName: '',
    },
    signOutInfo: {
      label: '',
      url: '',
    },
    languageInfo: {
      currentLocale: '',
      label: '',
      languageOptions: [],
      changeLanguageText: '',
    },
    notificationSettingsInfo: {
      url: '',
      label: '',
    },
    userSettingsInfo: {
      url: '',
      label: '',
    },
  },
  helpInfo: [],
  spoofingInfo: {
    isSpoofing: '',
    usernameWithSpoofing: '',
    quitButton: {
      label: '',
      url: '',
    },
  },
  isBackUpNav: false,
  hieConsoleLevelBannerCdn: '',
  hasSalesAssistantLoaded: false,
};

export const headerSlice = createSlice({
  name: 'header',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: headerInitialState,
  reducers: {
    initHeaderState: (state, action: PayloadAction<HeaderState>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateUserInfo: (state, action: PayloadAction<UserInfo>) => {
      return {
        ...state,
        userInfo: {
          ...action.payload,
        },
      };
    },
    updateHasSalesAssistantLoaded: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasSalesAssistantLoaded: action.payload,
      };
    },
  },
});

export const {
  initHeaderState,
  updateUserInfo,
  updateHasSalesAssistantLoaded,
} = headerSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectHeaderState = (state: RootState): HeaderState =>
  state.header;

export const selectUserInfoFromHeader = (state: RootState): UserInfo => {
  return state.header.userInfo;
};

export const selectHelpMenu = (state: RootState): Array<LinkProps> => {
  return state.header.helpInfo;
};

export const selectSalesAssistantLoading = (state: RootState): boolean => {
  return state.header.hasSalesAssistantLoaded || false;
};

export const selectIsSpoofing = (state: RootState): boolean => {
  return state.header.spoofingInfo?.isSpoofing === 'true';
};

export const selectCommunicationsDrawerInfo = (
  state: RootState,
): CommunicationsDrawerInfo | undefined => {
  return state.header.communicationsDrawerInfo;
};

export const selectCommunicationsDrawerLabel = (state: RootState): string => {
  return state.header.communicationsDrawerInfo?.texts?.label || '';
};

export const selectSpoofingInfo = (state: RootState): SpoofingInfo => {
  return state.header.spoofingInfo;
};

export const selectIsBackUpNav = (state: RootState): boolean => {
  return state.header.isBackUpNav;
};

export const selectSignOutInfo = (state: RootState): SignOutInfo => {
  return state.header.userInfo.signOutInfo;
};

export const selectHieConsoleLevelBannerCdn = (state: RootState): string => {
  return state.header.hieConsoleLevelBannerCdn || '';
};

export const selectCurrentAccountInfoFromHeader = (
  state: RootState,
): CurrentAccountInfo => {
  return state.header.currentAccountInfo || ({} as CurrentAccountInfo);
};

export default headerSlice.reducer;
